import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import {
  OnboardingData,
  UpdateOnboardingData,
} from '@nxsys/store/slices/prospect/types';
import React, { useEffect, useMemo, useState } from 'react';
import ProgressOnboarding from './ProgressOnboarding';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, AppState } from '@nxsys/store';
import { prospectActions } from '@nxsys/store/slices/prospect/reducer/prospectSlice';
import AppLoader from '@nxsys/components/AppLoader';
import { IRELAND } from '@nxsys/store/constants/keys';
import OnboardingStepList from './OnboardingStepList';
import useOnboardingSteps from './useOnboardingSteps';
import { showMessage } from '@nxsys/utils/message';

type ViewOnboardingProps = {
  isOpen: boolean;
  toggle: () => void;
  prospectId: number;
  refetchData: () => void;
};

const ViewOnboardingModal: React.FC<ViewOnboardingProps> = ({
  isOpen,
  toggle,
  prospectId,
  refetchData,
}: ViewOnboardingProps) => {
  const { messages } = useTypedIntl();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const isIreland = payrollCountry === IRELAND;

  const dispatch = useDispatch<AppDispatch>();

  // Initial fetch only — a step save shows its feedback on that step's button.
  const [loading, setLoading] = useState(false);
  const [submittingStepKey, setSubmittingStepKey] = useState<string | null>(
    null,
  );
  const [data, setData] = useState<
    OnboardingData | UpdateOnboardingData | null
  >(null);
  const [keyRefreshStep, setKeyRefreshStep] = useState(0);
  // Every onboarding endpoint returns `completed` alongside the payload: the
  // API has finalised the onboarding and rejects further edits.
  const [completed, setCompleted] = useState(false);

  const prospect = useMemo(() => data?.prospectOnboarding, [data]);
  const onboarding = useMemo(() => data?.onboarding, [data]);

  const { steps, completedSteps, totalSteps, nationality, setNationality } =
    useOnboardingSteps(data);

  // FE-BUG-007/013: Add cleanup to prevent setState on unmounted component
  useEffect(() => {
    let cancelled = false;
    const getData = async () => {
      setLoading(true);
      try {
        const res = await dispatch(
          prospectActions.getOnboardingByProspectId({ id: prospectId }),
        ).unwrap();
        if (!cancelled) {
          setData(res.data);
          setCompleted(!!res.completed);
        }
      } catch {
        // error handled by Redux
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    getData();
    return () => {
      cancelled = true;
    };
  }, [prospectId]);

  const handleSubmitStep = async (
    stepKey: string,
    values: any,
    options?: { hideToast?: boolean; skipComplete?: boolean },
  ) => {
    const hideToast = options?.hideToast;
    // A finalised onboarding is read-only on the API side, so a save would fail
    // (or silently do nothing) while the slice still toasted "updated
    // successfully" — say why nothing was saved instead.
    if (completed) {
      showMessage({
        type: 'warning',
        content: (messages['prospect.onboarding.completedCannotUpdate'] ??
          'Onboarding is already completed. The data can no longer be updated') as string,
      });
      return;
    }
    try {
      setSubmittingStepKey(stepKey);
      const currentStep: string = stepKey;
      let progressStep: string = stepKey;
      // FE-BUG-011: Add null check for steps[currentStep] to prevent crash
      if (
        onboarding &&
        steps[currentStep]?.index < steps[onboarding.progressStep]?.index
      ) {
        progressStep = steps[onboarding.progressStep ?? '']?.key ?? stepKey;
      }

      // A background save (the "I don't have a Passport / Share Code"
      // checkbox) only records the flag — the step itself stays pending — so it
      // must go through the normal step endpoint even when it is the last
      // outstanding step, never the "success" one that closes the onboarding.
      const isCompleteLastStep =
        !options?.skipComplete &&
        Object.values(steps)
          .filter((step) => step.key !== stepKey)
          .filter((step) => !step.completed).length === 0;

      let res = null;
      if (!onboarding) {
        res = await dispatch(
          prospectActions.createOnboardingByProspectId({
            prospectId: prospectId,
            currentStep,
            progressStep,
            ...values,
            hideToast,
          } as any),
        ).unwrap();
      } else if (isCompleteLastStep) {
        res = await dispatch(
          prospectActions.updateSuccessOnboardingByProspectId({
            prospectId: prospectId,
            currentStep,
            progressStep,
            ...values,
            hideToast,
          } as any),
        ).unwrap();
      } else {
        res = await dispatch(
          prospectActions.updateOnboardingById({
            id: onboarding.id,
            payload: {
              currentStep,
              progressStep,
              ...values,
            },
            isIreland,
            hideToast,
          } as any),
        ).unwrap();
      }
      setData(res.data);
      setCompleted(!!(res as any)?.completed);
      setKeyRefreshStep((prev) => prev + 1);
      setSubmittingStepKey(null);
      if (isCompleteLastStep) {
        toggle();
      }
      refetchData();
    } catch (err) {
      console.info(err);
      setSubmittingStepKey(null);
    }
  };

  return (
    <AppModalContainer
      title={
        <AppPageTitle
          prefix={messages['prospect.onboarding.title']}
          separator={''}
          title={`${prospect?.forename ?? ''} ${prospect?.surname ?? ''}`}
        />
      }
      isOpen={isOpen}
      toggle={toggle}
      width={800}
    >
      {loading && <AppLoader />}
      {data && (
        <>
          <ProgressOnboarding
            className="mb-4"
            completed={completedSteps}
            total={totalSteps}
          />
          <OnboardingStepList
            data={data}
            steps={steps}
            nationality={nationality}
            onChangeNationality={setNationality}
            onSubmitStep={handleSubmitStep}
            refreshKey={keyRefreshStep}
            submittingStepKey={submittingStepKey}
          />
        </>
      )}
    </AppModalContainer>
  );
};

export default ViewOnboardingModal;
